import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import useApi from '../api';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormatter';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingState } from '@/components/ui/LoadingState';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Eye, Loader2, UserPlus } from 'lucide-react';

interface VisitorLead {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  company?: string | null;
  job_title?: string | null;
}

interface VisitorProfileResponse {
  id: string;
  visitor_id: string | null;
  organization_id: string | null;
  organization_name?: string | null;
  lead_id?: string | null;
  account_id?: string | null;
  lead?: VisitorLead | null;
  company?: { name?: string | null; [key: string]: unknown } | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  last_activity_at?: string | null;
  conversation_count?: number | null;
  session_count?: number | null;
  [key: string]: unknown;
}

interface VisitorConversation {
  id: string;
  name?: string | null;
  type?: string | null;
  status?: string | null;
  agent_id?: string | null;
  organization_id?: string | null;
  organization_name?: string | null;
  lead_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  message_count?: number;
}

interface BrowserEventRow {
  id: string;
  organization_id?: string | null;
  visitor_id?: string | null;
  conversation_id?: string | null;
  journey_session_id?: string | null;
  surface?: string | null;
  event_type?: string | null;
  occurred_at?: string | null;
  url?: string | null;
  path?: string | null;
  referrer_url?: string | null;
  element_tag?: string | null;
  element_name?: string | null;
  element_text?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string | null;
}

interface VisitorDetailResponse {
  profile: VisitorProfileResponse;
  conversations: VisitorConversation[];
  browser_event_count: number;
}

const renderJsonBlock = (data: unknown) => (
  <pre className="text-xs bg-muted p-3 rounded-lg overflow-auto max-h-[600px]">
    {JSON.stringify(data, null, 2)}
  </pre>
);

const VisitorView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, post } = useApi();

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<VisitorDetailResponse | null>(null);
  const [syncingContact, setSyncingContact] = useState(false);

  const [events, setEvents] = useState<BrowserEventRow[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState<BrowserEventRow | null>(null);
  const eventsPerPage = 50;

  const fetchVisitor = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await get(`/admin/visitors/${id}`);
      setDetail(response.data);
    } catch (error) {
      console.error('Error fetching visitor:', error);
      toast.error('Failed to load visitor');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async (pageNum: number) => {
    if (!id) return;
    try {
      setEventsLoading(true);
      const response = await get(
        `/admin/visitors/${id}/browser-events?page=${pageNum}&per_page=${eventsPerPage}`
      );
      setEvents(response.data.items || []);
      setEventsTotalPages(response.data.pages || 1);
      setEventsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching browser events:', error);
      toast.error('Failed to load browser events');
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitor();
  }, [id]);

  useEffect(() => {
    fetchEvents(eventsPage);
  }, [id, eventsPage]);

  const conversationColumns: ColumnDef<VisitorConversation>[] = [
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) =>
        row.original.created_at ? formatDate(row.original.created_at) : 'N/A',
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => row.original.name || '—',
    },
    {
      accessorKey: 'organization_name',
      header: 'Organization',
      cell: ({ row }) => row.original.organization_name || 'N/A',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.status === 'active' ? 'default' : 'secondary'}>
          {row.original.status || 'unknown'}
        </Badge>
      ),
    },
    {
      accessorKey: 'message_count',
      header: 'Messages',
      cell: ({ row }) => row.original.message_count ?? 0,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/conversations/${row.original.id}`)}
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
      ),
    },
  ];

  const eventColumns: ColumnDef<BrowserEventRow>[] = [
    {
      accessorKey: 'occurred_at',
      header: 'When',
      cell: ({ row }) =>
        row.original.occurred_at ? formatDate(row.original.occurred_at) : '—',
    },
    {
      accessorKey: 'surface',
      header: 'Surface',
      cell: ({ row }) => (
        <Badge variant="outline">{row.original.surface || '—'}</Badge>
      ),
    },
    {
      accessorKey: 'event_type',
      header: 'Type',
      cell: ({ row }) => (
        <Badge variant="secondary">{row.original.event_type || '—'}</Badge>
      ),
    },
    {
      accessorKey: 'url',
      header: 'URL',
      cell: ({ row }) => {
        const value = row.original.url || row.original.path || '';
        if (!value) return '—';
        return (
          <span className="text-xs font-mono truncate block max-w-[340px]" title={value}>
            {value}
          </span>
        );
      },
    },
    {
      id: 'element',
      header: 'Element',
      cell: ({ row }) => {
        const parts = [
          row.original.element_tag,
          row.original.element_name,
          row.original.element_text,
        ].filter(Boolean);
        if (!parts.length) return '—';
        return (
          <span className="text-xs truncate block max-w-[260px]" title={parts.join(' · ')}>
            {parts.join(' · ')}
          </span>
        );
      },
    },
  ];

  const conversationTable = useReactTable({
    data: detail?.conversations ?? [],
    columns: conversationColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const eventTable = useReactTable({
    data: events,
    columns: eventColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading && !detail) {
    return <LoadingState label="Loading visitor..." className="min-h-[60vh]" />;
  }

  if (!detail) {
    return (
      <div className="px-6 py-4 w-full">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to admin
        </Button>
        <div className="mt-6 text-muted-foreground">Visitor not found.</div>
      </div>
    );
  }

  const profile = detail.profile;
  const displayName = (() => {
    if (profile.lead) {
      const parts = [profile.lead.first_name, profile.lead.last_name].filter(Boolean).join(' ').trim();
      if (parts) return parts;
      if (profile.lead.email) return profile.lead.email;
    }
    const person = (profile as Record<string, unknown>).person as Record<string, unknown> | undefined;
    if (person) {
      const fullName = typeof person.full_name === 'string' ? person.full_name : null;
      if (fullName) return fullName;
      const email = typeof person.email === 'string' ? person.email : null;
      if (email) return email;
    }
    return 'Anonymous visitor';
  })();

  // A profile with an email/phone but no linked contact can be synced
  // manually — recovery path for contact info that never became a lead.
  // unverified_email counts: the human clicking sync overrides the verifier.
  const profileAttributes =
    (profile.profile_attributes as Record<string, unknown> | null | undefined) || {};
  const hasContactInfo = Boolean(
    profileAttributes.email || profileAttributes.phone || profileAttributes.unverified_email
  );
  const canSyncContact = !profile.lead_id && hasContactInfo;

  const handleSyncContact = async () => {
    if (!id || syncingContact) return;
    setSyncingContact(true);
    try {
      const orgParam = profile.organization_id
        ? `?organization_id=${profile.organization_id}`
        : '';
      const response = await post(`/admin/visitors/${id}/sync-lead${orgParam}`, {});
      toast.success(
        response?.data?.is_new ? 'Contact created and synced.' : 'Contact synced.'
      );
      await fetchVisitor();
    } catch (error: any) {
      const detailMessage = error?.response?.data?.detail;
      toast.error(
        typeof detailMessage === 'string' ? detailMessage : 'Failed to sync contact.'
      );
    } finally {
      setSyncingContact(false);
    }
  };

  return (
    <div className="px-6 py-4 w-full space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to admin
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <CardTitle className="flex flex-col gap-1">
              <span className="text-xl">{displayName}</span>
              <span className="text-xs font-mono text-muted-foreground">
                {profile.visitor_id}
              </span>
            </CardTitle>
            {canSyncContact && (
              <Button
                size="sm"
                variant="outline"
                disabled={syncingContact}
                onClick={handleSyncContact}
              >
                {syncingContact ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                )}
                Sync Contact
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground text-xs">Organization</div>
              <div>{profile.organization_name || '—'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">First seen</div>
              <div>{profile.first_seen_at ? formatDate(profile.first_seen_at) : '—'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Last seen</div>
              <div>{profile.last_seen_at ? formatDate(profile.last_seen_at) : '—'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Conversations</div>
              <div>{profile.conversation_count ?? detail.conversations.length}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Sessions</div>
              <div>{profile.session_count ?? '—'}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Browser events</div>
              <div>{detail.browser_event_count}</div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Location</div>
              <div>
                {[profile.city, profile.region, profile.country]
                  .filter((v): v is string => typeof v === 'string' && v.length > 0)
                  .join(', ') || '—'}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">Company</div>
              <div>
                {(() => {
                  const companyName =
                    (profile.company as { name?: string } | null | undefined)?.name ||
                    profile.lead?.company ||
                    null;
                  const accountId = profile.account_id as string | null | undefined;
                  if (accountId) {
                    return (
                      <Link to={`/accounts/${accountId}`} className="hover:underline text-blue-500">
                        {companyName || 'View account'}
                      </Link>
                    );
                  }
                  return companyName || '—';
                })()}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">IP</div>
              <div className="font-mono text-xs">
                {typeof profile.ip_address === 'string' ? profile.ip_address : '—'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Profile JSON</CardTitle>
        </CardHeader>
        <CardContent>{renderJsonBlock(profile)}</CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Related conversations ({detail.conversations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                {conversationTable.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {conversationTable.getRowModel().rows?.length ? (
                  conversationTable.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={conversationColumns.length} className="h-24 text-center">
                      No conversations for this visitor.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Browser events ({eventsTotal})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                {eventTable.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {eventTable.getRowModel().rows?.length ? (
                  eventTable.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedEvent(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={eventColumns.length} className="h-24 text-center">
                      {eventsLoading ? 'Loading events…' : 'No browser events for this visitor.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Page {eventsPage} of {eventsTotalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEventsPage((p) => Math.max(1, p - 1))}
                disabled={eventsPage === 1 || eventsLoading}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEventsPage((p) => Math.min(eventsTotalPages, p + 1))}
                disabled={eventsPage === eventsTotalPages || eventsLoading}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selectedEvent} onOpenChange={(open) => !open && setSelectedEvent(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Browser event
              {selectedEvent?.event_type ? ` · ${selectedEvent.event_type}` : ''}
              {selectedEvent?.occurred_at ? ` · ${formatDate(selectedEvent.occurred_at)}` : ''}
            </DialogTitle>
          </DialogHeader>
          {selectedEvent ? renderJsonBlock(selectedEvent) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VisitorView;
